import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useCreateSponsorship } from "@/api/sponsorships";
import PropTypes from "prop-types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import axios from "@/lib/axios";

const formSchema = z
  .object({
    type: z.enum(["Mahallu", "Individual"], {
      required_error: "Sponsorship type is required",
    }),
    sponsorName: z.string().min(2, { message: "Sponsor name is required" }),
    sponsorPhone: z.string().optional().or(z.literal("")),
    chapter: z.string().min(1, { message: "Chapter is required" }),
    amountPromised: z.string().min(1, { message: "Amount is required" }),
    currency: z.string().default("INR"),
    careOfName: z.string().optional().or(z.literal("")),
    careOfPhone: z.string().optional().or(z.literal("")),
    mahalluName: z.string().optional().or(z.literal("")),
    place: z.string().optional().or(z.literal("")),
  })
  .refine(
    (data) => {
      if (data.type === "Mahallu" && !data.mahalluName) return false;
      return true;
    },
    { message: "Mahallu name is required", path: ["mahalluName"] },
  )
  .refine(
    (data) => {
      if (data.type === "Individual" && !data.place) return false;
      return true;
    },
    { message: "Place is required", path: ["place"] },
  );

export function AddSponsorshipForm({ onSuccess }) {
  const { mutate: createSponsorship, isPending } = useCreateSponsorship();

  const { data: chapters, isLoading: isLoadingChapters } = useQuery({
    queryKey: ["chapters"],
    queryFn: async () => {
      const response = await axios.get("/chapters");
      return response.data.sort((a, b) =>
        a.chapterName.localeCompare(b.chapterName),
      );
    },
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      type: "",
      sponsorName: "",
      sponsorPhone: "",
      chapter: "",
      amountPromised: "",
      currency: "INR",
      careOfName: "",
      careOfPhone: "",
      mahalluName: "",
      place: "",
    },
  });

  const selectedType = form.watch("type");
  const selectedChapterId = form.watch("chapter");

  // Get mahallu list for selected chapter
  const selectedChapter = chapters?.find((c) => c._id === selectedChapterId);
  const mahalluOptions = selectedChapter?.mahalluList || [];

  function onSubmit(values) {
    const submitData = {
      type: values.type,
      sponsorName: values.sponsorName,
      sponsorPhone: values.sponsorPhone || undefined,
      chapter: values.chapter,
      amountPromised: parseInt(values.amountPromised, 10),
      currency: values.currency,
      careOf: {
        name: values.careOfName || undefined,
        phone: values.careOfPhone || undefined,
      },
    };

    if (values.type === "Mahallu") {
      submitData.mahalluName = values.mahalluName;
    } else {
      submitData.place = values.place;
    }

    createSponsorship(submitData, {
      onSuccess: () => {
        form.reset();
        onSuccess?.();
      },
    });
  }

  return (
    <div className="max-h-[calc(100vh-200px)] overflow-y-auto px-1">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          {/* Type & Chapter Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-muted-foreground">
                Sponsorship Type
              </h3>
              <div className="h-px flex-1 bg-border" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-11 sm:h-8">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Mahallu">Mahallu</SelectItem>
                        <SelectItem value="Individual">Individual</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="chapter"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Chapter</FormLabel>
                    <Select
                      disabled={isLoadingChapters}
                      onValueChange={(value) => {
                        field.onChange(value);
                        // Reset mahallu selection when chapter changes
                        form.setValue("mahalluName", "");
                      }}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="h-11 sm:h-8">
                          <SelectValue placeholder="Select chapter" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {chapters?.map((chapter) => (
                          <SelectItem key={chapter._id} value={chapter._id}>
                            {chapter.chapterName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Conditional: Mahallu Name or Place */}
            {selectedType === "Mahallu" && (
              <FormField
                control={form.control}
                name="mahalluName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mahallu Name</FormLabel>
                    {mahalluOptions.length > 0 ? (
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="h-11 sm:h-8">
                            <SelectValue placeholder="Select mahallu" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {mahalluOptions.map((name) => (
                            <SelectItem key={name} value={name}>
                              {name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <FormControl>
                        <Input
                          placeholder="No mahallu configured for this chapter"
                          disabled
                          className="h-11 sm:h-8"
                        />
                      </FormControl>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {selectedType === "Individual" && (
              <FormField
                control={form.control}
                name="place"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Place</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter place"
                        {...field}
                        className="h-11 sm:h-8"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>

          {/* Sponsor Details */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-muted-foreground">
                Sponsor Details
              </h3>
              <div className="h-px flex-1 bg-border" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="sponsorName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sponsor Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter sponsor name"
                        {...field}
                        className="h-11 sm:h-8"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="sponsorPhone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone (Optional)</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter phone number"
                        {...field}
                        className="h-11 sm:h-8"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Amount Details */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-muted-foreground">
                Amount Details
              </h3>
              <div className="h-px flex-1 bg-border" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="amountPromised"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amount Promised</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="Enter amount"
                        {...field}
                        className="h-11 sm:h-8"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Currency</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="h-11 sm:h-8">
                          <SelectValue placeholder="Select currency" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="INR">₹ INR</SelectItem>
                        <SelectItem value="SAR">﷼ SAR</SelectItem>
                        <SelectItem value="QAR">ر.ق QAR</SelectItem>
                        <SelectItem value="AED">د.إ AED</SelectItem>
                        <SelectItem value="KWD">د.ك KWD</SelectItem>
                        <SelectItem value="OMR">ر.ع. OMR</SelectItem>
                        <SelectItem value="BHD">.د.ب BHD</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Care Of Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-muted-foreground">
                Care Of (Optional)
              </h3>
              <div className="h-px flex-1 bg-border" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="careOfName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>C/O Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter care of name"
                        {...field}
                        className="h-11 sm:h-8"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="careOfPhone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>C/O Phone</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter care of phone"
                        {...field}
                        className="h-11 sm:h-8"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Submit */}
          <div className="sticky bottom-0 pt-4 pb-2 bg-background">
            <Button
              type="submit"
              className="w-full h-12 sm:h-8 text-base sm:text-sm rounded-lg"
              disabled={isPending}
            >
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Creating Sponsorship...
                </>
              ) : (
                "Create Sponsorship"
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

AddSponsorshipForm.propTypes = {
  onSuccess: PropTypes.func,
};
